import type {
  Form,
  FormField,
  FormNavigationAction,
  FormSection,
} from '../../types/form';

export type NextSectionResult =
  | { kind: 'section'; sectionId: string }
  | { kind: 'submit' };

export function sortSections(sections: FormSection[]): FormSection[] {
  return sections.slice().sort((a, b) => a.order - b.order);
}

export function getSectionFields(
  fields: FormField[],
  sectionId: string
): FormField[] {
  return fields
    .filter((field) => field.sectionId === sectionId)
    .sort((a, b) => a.order - b.order);
}

function resolveAction(
  action: FormNavigationAction,
  targetSectionId: string | null | undefined,
  sortedSections: FormSection[],
  currentIndex: number
): NextSectionResult {
  if (action === 'SubmitForm') {
    return { kind: 'submit' };
  }

  if (action === 'GoToSection') {
    const target = sortedSections.find((s) => s.id === targetSectionId);
    if (target) {
      return { kind: 'section', sectionId: target.id };
    }
  }

  const next = sortedSections[currentIndex + 1];
  if (next) {
    return { kind: 'section', sectionId: next.id };
  }

  return { kind: 'submit' };
}

export function resolveNextSection(
  form: Form,
  currentSectionId: string,
  answers: Record<string, string>
): NextSectionResult {
  const sortedSections = sortSections(form.sections);
  const currentIndex = sortedSections.findIndex(
    (section) => section.id === currentSectionId
  );

  if (currentIndex < 0) {
    return { kind: 'submit' };
  }

  const currentSection = sortedSections[currentIndex];
  const sectionFields = getSectionFields(form.fields, currentSectionId);

  const branchingFields = sectionFields.filter(
    (field) =>
      field.goToSectionBasedOnAnswer &&
      (field.type === 'MultipleChoice' || field.type === 'Dropdown')
  );

  for (let index = branchingFields.length - 1; index >= 0; index -= 1) {
    const field = branchingFields[index];
    const selectedValue = answers[field.id];
    if (!selectedValue) {
      continue;
    }

    const selectedOption = field.options.find(
      (option) => option.label === selectedValue
    );
    if (selectedOption && selectedOption.goToAction) {
      return resolveAction(
        selectedOption.goToAction,
        selectedOption.goToTargetSectionId,
        sortedSections,
        currentIndex
      );
    }
  }

  return resolveAction(
    currentSection.afterSectionAction ?? 'ContinueToNext',
    currentSection.afterSectionTargetId,
    sortedSections,
    currentIndex
  );
}
